'use client'
import { useState, useRef, useEffect } from 'react'
import { BookOpen, Link as LinkIcon } from 'lucide-react'
import { IntegrationCardLayout } from '@/components/integrations/integration-card-layout'
import { KnowledgeSourceProviderIcon } from './provider-icon'
import { useSearchParams } from 'next/navigation'
import { useTranslations, useFormatter } from 'next-intl'
import { usePermissions } from '@/hooks/use-permissions'
import { AI_PERMISSIONS, VOICE_AGENTS_PERMISSIONS, EMAIL_AGENTS_PERMISSIONS, MESSAGING_AGENTS_PERMISSIONS } from '@/lib/auth/permissions.constants'
import { useKnowledgeSourcesQuery } from '@/hooks/queries/knowledge-sources'
import { useKnowledgeSourceMutation } from '@/hooks/mutations/knowledge-sources'
import { knowledgeSourceProviderFlows } from './provider-flows'
import { TranslationText } from '@/components/i18n/translation-text'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import type { KnowledgeAgentRef } from '@/lib/knowledge-sources/types'
import { KnowledgeSourceDisconnectDialog } from './disconnect-dialog'
import { KnowledgeSourceConnectDialog } from './connect-dialog'
import { KnowledgeSourceConnectionCard } from './connection-card'
import { knowledgeSourceStatusLabel } from './status-label'

const AGENT_MANAGE_PERMISSIONS = { voice: VOICE_AGENTS_PERMISSIONS.MANAGE, email: EMAIL_AGENTS_PERMISSIONS.MANAGE, message: MESSAGING_AGENTS_PERMISSIONS.MANAGE } as const

export function KnowledgeSourcesPanel({ agent, entryPoint = 'knowledge', onKnowledgeChanged, presentation = 'panel' }: { agent?: KnowledgeAgentRef; entryPoint?: 'integrations' | 'knowledge'; onKnowledgeChanged?: () => void; presentation?: 'panel' | 'connect-button' }) {
  const t = useTranslations('KnowledgeSources')
  const format = useFormatter()
  const query = useKnowledgeSourcesQuery()
  const mutation = useKnowledgeSourceMutation()
  const { can } = usePermissions()
  const params = useSearchParams()
  const resumeId = params.get('knowledgeConnection') ?? undefined
  const resumeType = params.get('knowledgeAgentType')
  const resumeAgentId = params.get('knowledgeAgentId')
  const resumeAgent: KnowledgeAgentRef | undefined = resumeAgentId && (resumeType === 'voice' || resumeType === 'email' || resumeType === 'message') ? { type: resumeType, id: resumeAgentId } : undefined
  const [dialog, setDialog] = useState<{ connectionId?: string; folderId?: string } | null>(resumeId ? { connectionId: resumeId } : null)
  const [disconnectId, setDisconnectId] = useState<string>()
  const [error, setError] = useState(false)
  const canManage = can(AI_PERMISSIONS.MANAGE_KNOWLEDGE_BASE) && (!agent || can(AGENT_MANAGE_PERMISSIONS[agent.type]))
  const onChange = useRef(onKnowledgeChanged)
  const lastFingerprint = useRef<string | undefined>(undefined)
  const fingerprint = query.data ? JSON.stringify({ sources: query.data.sources, connections: query.data.connections.map(row => ({ id: row.id, status: row.status })) }) : undefined
  useEffect(() => { onChange.current = onKnowledgeChanged }, [onKnowledgeChanged])
  useEffect(() => {
    if (!fingerprint || fingerprint === lastFingerprint.current) return
    lastFingerprint.current = fingerprint
    onChange.current?.()
  }, [fingerprint])
  if (!query.data) return null
  async function reconnect(connectionId: string) {
    try {
      const connection = query.data?.connections.find(row => row.id === connectionId)
      if (!connection) throw new Error('Knowledge source connection not found.')
      await knowledgeSourceProviderFlows[connection.provider].connect({ returnTo: entryPoint === 'integrations' ? 'integrations' : 'knowledge-base', connectionId, agent })
      await query.refetch()
    } catch { setError(true) }
  }
  function close(open: boolean) {
    if (open) return
    setDialog(null)
    if (resumeId) {
      const url = new URL(window.location.href)
      for (const key of ['knowledgeConnection', 'knowledgeAgentType', 'knowledgeAgentId']) url.searchParams.delete(key)
      window.history.replaceState(null, '', url)
    }
  }
  const notice = (error || params.get('knowledgeConnectionError')) && <p role="alert" className="text-sm text-destructive"><span>{t('connectionFailed')}</span></p>
  const addButton = canManage && <Button variant={entryPoint === 'integrations' ? 'default' : 'outline'}
    className={entryPoint === 'integrations' ? 'h-auto min-h-10 max-w-full whitespace-normal text-left' : undefined} onClick={() => setDialog({})}>
    {entryPoint === 'integrations' && <LinkIcon aria-hidden="true" className="mr-2 h-4 w-4" />}
    <span>{entryPoint === 'integrations' ? t('addNew') : t('connect')}</span>
  </Button>
  const dialogs = <>
    {disconnectId && <KnowledgeSourceDisconnectDialog connectionId={disconnectId} onClose={() => setDisconnectId(undefined)} />}
    {dialog && <KnowledgeSourceConnectDialog open onOpenChange={close} returnTo={entryPoint === 'integrations' ? 'integrations' : 'knowledge-base'} connectionId={dialog.connectionId} folderId={dialog.folderId} agent={agent ?? resumeAgent} />}
  </>
  if (presentation === 'connect-button') return <>
    {canManage && <Button variant="outline" aria-label={t('connect')} title={t('connect')} onClick={() => setDialog({})}>
      <span>{t('connectShort')}</span><KnowledgeSourceProviderIcon provider="google_drive" />
    </Button>}
    {notice}
    {dialogs}
  </>
  const content = <div className="space-y-4">
      {notice}
      {!query.data.sources.length && <p className="text-sm text-muted-foreground"><span>{t('empty')}</span></p>}
      {entryPoint === 'integrations' && <div>{addButton}</div>}
      {query.data.connections.map(connection => <KnowledgeSourceConnectionCard key={connection.id} connection={connection} canManage={canManage}
        onReconnect={() => void reconnect(connection.id)} onDisconnect={() => setDisconnectId(connection.id)} />)}
      {query.data.sources.map(source => {
        const connected = query.data?.connections.some(connection => connection.id === source.connectionId && connection.status === 'connected')
        const assigned = Boolean(agent && source.agents.some(ref => ref.id === agent.id && ref.type === agent.type))
        return <div key={source.id} className="space-y-3 rounded-md border p-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <p className="min-w-0 break-words font-medium"><span key={source.name}>{source.name}</span></p>
            {(source.status === 'disconnecting' || source.cleanupPendingCount > 0) && <p className="text-sm"><span>{t('cleanupPending')}</span></p>}
            {canManage && source.status === 'active' && <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" disabled={mutation.isPending || !connected} onClick={() => setDialog({ connectionId: source.connectionId!, folderId: source.id })}><span>{t('addFiles')}</span></Button>
              <Button variant="outline" size="sm" disabled={mutation.isPending || !connected} onClick={() => void mutation.mutateAsync({ kind: 'sync', folderId: source.id }).catch(() => {})}><span>{t('syncNow')}</span></Button>
              {agent && <Button variant={assigned ? 'secondary' : 'outline'} size="sm" disabled={mutation.isPending} onClick={() => void mutation.mutateAsync({ kind: 'assign', folderId: source.id, agent, assign: !assigned }).catch(() => {})}><span key={String(assigned)}>{assigned ? t('unassign') : t('assign')}</span></Button>}
            </div>}
          </div>
          <ul className="space-y-2">{source.items.map(item => <li key={item.id} className="flex flex-wrap items-start justify-between gap-2 text-sm">
            <div className="min-w-0 flex-1"><a className="break-words underline" href={item.sourceUrl ?? undefined} target="_blank" rel="noopener noreferrer"><span key={item.name}>{item.name}</span></a>
              <p className="text-muted-foreground"><span key={item.status}>{knowledgeSourceStatusLabel(item.status, t)}</span></p>
              {item.lastSyncedAt && <p className="text-muted-foreground"><TranslationText>{t('lastSync', { date: format.dateTime(new Date(item.lastSyncedAt), { dateStyle: 'short', timeStyle: 'short' }) })}</TranslationText></p>}
              {item.lastError && <p className="text-destructive"><span>{t('fileAttention')}</span></p>}
            </div>
            {canManage && <Button variant="ghost" size="sm" disabled={mutation.isPending} onClick={() => void mutation.mutateAsync({ kind: 'remove', itemId: item.id }).catch(() => {})}><span>{t('remove')}</span></Button>}
          </li>)}</ul>
        </div>
      })}
  </div>
  if (entryPoint === 'integrations') return <section id="knowledge-sources">
    <IntegrationCardLayout title={t('title')} description={t('description')} icon={<BookOpen className="h-5 w-5" />} defaultOpen={Boolean(resumeId || params.get('knowledgeConnectionError'))}>
      {content}
    </IntegrationCardLayout>
    {dialogs}
  </section>
  return <Card id="knowledge-sources">
    <CardHeader><div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 space-y-1"><CardTitle><span>{t('title')}</span></CardTitle><CardDescription><span>{t('description')}</span></CardDescription></div>
      {addButton}
    </div></CardHeader>
    <CardContent>{content}</CardContent>
    {dialogs}
  </Card>
}
